// Standalone Nav + Footer chrome extracted from _v10_mapled.jsx so that
// interior pages can mount the same header/footer used on the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#f4f4f0', fg: '#1c1f1c', muted: '#5c625b',
  border: '#d8d6cd', accent: '#2d6a4f', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 8,
  font: '"DM Sans", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const PinGlyph = ({ size = 12, color = theme.accent }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" style={{ flexShrink: 0 }} aria-hidden>
    <path d="M6 1 C 3.8 1 2 2.7 2 4.9 C 2 7.6 6 11 6 11 C 6 11 10 7.6 10 4.9 C 10 2.7 8.2 1 6 1 Z M 6 6.2 A 1.4 1.4 0 1 1 6 3.4 A 1.4 1.4 0 0 1 6 6.2 Z"
      fill={color} />
  </svg>
);

const defaultNavLinks = [
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Partner',       href: '/partner' },
  { label: 'Förderung',     href: '/förderung-finder' },
  { label: 'Kontakt',       href: '/kontakt' },
];

const defaultFooterLinks = [
  { label: 'Impressum',   href: '/impressum' },
  { label: 'Datenschutz', href: '/datenschutz' },
  { label: 'AGB',         href: '/agb' },
  { label: 'Widerruf',    href: '/widerruf' },
  { label: 'Kontakt',     href: '/kontakt' },
];

export function V10MapledNav({ brand = 'Waemo', navLinks = defaultNavLinks, brandLogo = null }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const brandLabel = brand || 'WAEMO Energie';
  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '12px 16px' : '14px 32px',
      borderBottom: `1px solid ${theme.border}`, background: theme.bg,
      fontFamily: theme.font, color: theme.fg, position: 'relative',
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: 'inherit', textDecoration: 'none' }} aria-label={brandLabel + ' Startseite'}>
        {brandLogo
          ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
          : <>{brandLabel}</>
        }
      </Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 22, fontSize: 13, color: theme.muted }}>
            {navLinks.map(l => (
              <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </div>
          <Link href="/angebot-vergleichen" style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            fontFamily: theme.font, textDecoration: 'none', display: 'inline-block',
          }}>Angebote vergleichen</Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 14px', fontSize: 14, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 6,
            textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
          }}>Angebote vergleichen</Link>
        </div>
      )}
    </nav>
  );
}

export function V10MapledFooter({ brand = 'Waemo', footerLinks = defaultFooterLinks, copyright, brandLogo = null }) {
  const isMobile = useIsMobile();
  const brandLabel = brand || 'WAEMO Energie';
  const resolvedCopyright = copyright != null ? copyright : `© ${new Date().getFullYear()} ${brandLabel}`;
  return (
    <footer style={{
      padding: isMobile ? '24px 16px' : '40px 48px',
      background: '#fff',
      borderTop: `1px solid ${theme.border}`,
      fontFamily: theme.font,
      color: theme.fg,
    }}>
      <div style={{
        maxWidth: 1184, margin: '0 auto',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap', gap: isMobile ? 14 : 24,
      }}>
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: theme.fg }} aria-label={brandLabel + ' Startseite'}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: theme.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PinGlyph size={12} color={theme.accentFg} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 600 }}>{brandLabel}</span>
              </>
          }
        </Link>
        <nav style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 10 : 28,
        }}>
          {footerLinks.map(l => (
            <Link key={l.label} href={l.href} style={{
              fontSize: 14, color: theme.muted,
              textDecoration: 'none',
            }}>{l.label}</Link>
          ))}
        </nav>
      </div>
      <div style={{
        maxWidth: 1184, margin: isMobile ? '16px auto 0' : '24px auto 0',
        fontSize: 12, color: theme.muted,
      }}>
        {resolvedCopyright}
      </div>
    </footer>
  );
}
